import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Alert, Button, ReportDateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'helpers/dayjs';
import { FiRefreshCw, FiFilter, FiBriefcase, FiGlobe, FiMapPin, FiMap, FiAlertCircle } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';

import JobEntrySummaryCards       from './JobEntrySummaryCards';
import JobEntryCategoryChart      from './JobEntryCategoryChart';
import JobEntryDomesticOverseaChart from './JobEntryDomesticOverseaChart';
import JobEntryDetailedTable      from './JobEntryDetailedTable';
import JobEntryFilterSidebar      from './JobEntryFilterSidebar';
import JobEntryDrillDownPanel     from './JobEntryDrillDownPanel';
import QuotationTrendChart     from '../quotations/QuotationTrendChart';
import QuotationBreakdownChart from '../quotations/QuotationBreakdownChart';

import {
  fetchJobEntrySummary,
  fetchJobEntryTrend,
  fetchJobEntryCategory,
  fetchJobEntryCategoryByType,
  fetchJobEntryRegion,
  fetchJobEntrySalesUnit,
  fetchJobEntryCountry,
  fetchJobEntryProvince,
  fetchJobEntryDomesticOversea,
  fetchJobEntryDetailed,
  fetchJobEntryFilterOptions,
} from 'redux/crm/dashboard/jobEntrySlice';

const DEFAULT_START = dayjs().startOf('year').format('YYYY-MM-DD');
const DEFAULT_END   = dayjs().format('YYYY-MM-DD');

// Sticks right below the Falcon top navbar (which is itself position: sticky)
const STICKY_BELOW_NAVBAR = { top: 'var(--falcon-top-nav-height, 69px)' };

const JobEntryDashboard = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('start_date') || DEFAULT_START,
    endDate:   searchParams.get('end_date')   || DEFAULT_END,
  });

  const [filters, setFilters] = useState({
    sales_units:     searchParams.get('sales_units')?.split(',').filter(Boolean).map(Number) || [],
    sales_incharges: searchParams.get('sales_incharges')?.split(',').filter(Boolean) || [],
    customers:       searchParams.get('customers')?.split(',').filter(Boolean).map(Number) || [],
    types_of_sales:  searchParams.get('types_of_sales')?.split(',').filter(Boolean) || [],
    cse_order_entries: searchParams.get('cse_order_entries')?.split(',').filter(Boolean) || [],
  });

  // Local country filter for the "By Province" breakdown only (not a global filter).
  const [provinceCountry, setProvinceCountry] = useState('');

  const [groupBy, setGroupBy]         = useState(searchParams.get('group_by') || 'month');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [detailedPage, setDetailedPage]   = useState(1);
  const [detailedPer, setDetailedPer]     = useState(50);
  const [tableSearch, setTableSearch]     = useState('');
  const [tableSort, setTableSort]         = useState({ by: 'job_entry_date', dir: 'DESC' });
  const [exportLoading, setExportLoading] = useState(false);

  const [drillDown, setDrillDown] = useState({ open: false, title: '', extraParams: {} });

  const {
    summary, trend, category, categoryByType,
    region, salesUnit, country, province, domesticOversea, detailed, filterOptions,
  } = useSelector(state => state.crm_dashboard_job_entry);

  const buildParams = useCallback((extra = {}) => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate, ...extra };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units;
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges;
    if (filters.customers.length)         p.customers         = filters.customers;
    if (filters.types_of_sales.length)    p.type_of_sales     = filters.types_of_sales;
    if (filters.cse_order_entries.length) p.cse_order_entries = filters.cse_order_entries;
    return p;
  }, [dateRange, filters]);

  const baseParams = useMemo(() => buildParams(), [buildParams]);

  const detailedParams = useMemo(() => ({
    ...baseParams,
    page:     detailedPage,
    per_page: detailedPer,
    q:        tableSearch || undefined,
    sort_by:  tableSort.by,
    sort_dir: tableSort.dir,
  }), [baseParams, detailedPage, detailedPer, tableSearch, tableSort]);

  // Sync URL params so filtered views are shareable
  useEffect(() => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate, group_by: groupBy };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units.join(',');
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges.join(',');
    if (filters.customers.length)         p.customers         = filters.customers.join(',');
    if (filters.types_of_sales.length)    p.types_of_sales    = filters.types_of_sales.join(',');
    if (filters.cse_order_entries.length) p.cse_order_entries = filters.cse_order_entries.join(',');
    setSearchParams(p);
  }, [dateRange, filters, groupBy, setSearchParams]);

  // Fetch all sections whenever filters change
  useEffect(() => {
    dispatch(fetchJobEntrySummary(baseParams));
    dispatch(fetchJobEntryCategory(baseParams));
    dispatch(fetchJobEntryCategoryByType(baseParams));
    dispatch(fetchJobEntryRegion(baseParams));
    dispatch(fetchJobEntrySalesUnit(baseParams));
    dispatch(fetchJobEntryCountry(baseParams));
    dispatch(fetchJobEntryDomesticOversea(baseParams));
    dispatch(fetchJobEntryFilterOptions({ sales_units: filters.sales_units }));
  }, [dispatch, baseParams, filters.sales_units]);

  useEffect(() => {
    dispatch(fetchJobEntryTrend({ ...baseParams, group_by: groupBy }));
  }, [dispatch, baseParams, groupBy]);

  // "By Province" breakdown re-fetches with its own local country filter.
  useEffect(() => {
    dispatch(fetchJobEntryProvince({ ...baseParams, country: provinceCountry || undefined }));
  }, [dispatch, baseParams, provinceCountry]);

  useEffect(() => {
    dispatch(fetchJobEntryDetailed(detailedParams));
  }, [dispatch, detailedParams]);

  const handleApplyFilters = (next) => {
    setFilters(next);
    setDetailedPage(1);
    setSidebarOpen(false);
  };

  const handleResetFilters = () => {
    setFilters({ sales_units: [], sales_incharges: [], customers: [], types_of_sales: [], cse_order_entries: [] });
    setDetailedPage(1);
  };

  const refreshAll = () => {
    dispatch(fetchJobEntrySummary(baseParams));
    dispatch(fetchJobEntryTrend({ ...baseParams, group_by: groupBy }));
    dispatch(fetchJobEntryCategory(baseParams));
    dispatch(fetchJobEntryCategoryByType(baseParams));
    dispatch(fetchJobEntryRegion(baseParams));
    dispatch(fetchJobEntrySalesUnit(baseParams));
    dispatch(fetchJobEntryCountry(baseParams));
    dispatch(fetchJobEntryDomesticOversea(baseParams));
    dispatch(fetchJobEntryProvince({ ...baseParams, country: provinceCountry || undefined }));
    dispatch(fetchJobEntryDetailed(detailedParams));
  };

  // ── Drill-down helpers ─────────────────────────────────────────────────────
  const openDrillDown = (title, extraParams) => setDrillDown({ open: true, title, extraParams });

  const handleTrendDrill = (row, gb) => {
    const start = dayjs(row.period);
    const end   = dayjs.min(start.clone().endOf(gb), dayjs());
    openDrillDown(`Period: ${row.label}`, {
      start_date: start.format('YYYY-MM-DD'),
      end_date:   end.format('YYYY-MM-DD'),
    });
  };

  // sales_unit_stats groups by name; resolve the id from filter options so the
  // drill-down filters by sales_units (the backend has no name filter).
  const handleUnitDrill = (title, extra) => {
    const name = extra?.sales_unit_name;
    const unit = (filterOptions.data?.sales_units || []).find(u => u.description === name || u.name === name);
    openDrillDown(title, unit ? { sales_units: [unit.id] } : {});
  };

  // ── Server-side Excel export (current filters + search + sort) ────────────
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/job_entries/export_detailed_data', {
        params: { ...baseParams, q: tableSearch || undefined, sort_by: tableSort.by, sort_dir: tableSort.dir },
        responseType: 'blob',
      });

      const contentType = res.data?.type || '';
      if (contentType.includes('json') || contentType.includes('html')) {
        const text = await res.data.text();
        let message = 'Export failed.';
        try { message = JSON.parse(text)?.error || message; } catch { /* keep default */ }
        window.alert(message);
        return;
      }

      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url;
      a.download = `job-entries-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let message = 'Export failed. Please try again.';
      try {
        const text = await e?.response?.data?.text?.();
        if (text) message = JSON.parse(text)?.error || message;
      } catch { /* keep default */ }
      window.alert(message);
    } finally {
      setExportLoading(false);
    }
  };

  const activeFilterCount =
    filters.sales_units.length + filters.sales_incharges.length +
    filters.customers.length + filters.types_of_sales.length + filters.cse_order_entries.length;

  const anyError = summary.error || category.error;

  return (
    <div className="tw-min-h-screen tw-bg-slate-50">
      {/* ── Sticky header (pinned below the app navbar) ── */}
      <div className="tw-sticky tw-z-30 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm" style={STICKY_BELOW_NAVBAR}>
        <div className="tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <div className="tw-flex-1 tw-min-w-0">
            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-0">Job Entry Dashboard</h1>
            <p className="tw-text-xs tw-text-gray-400 tw-hidden md:tw-block tw-mb-0">
              Signed (J) · Prospective (P) · Lost (L) · Trend · Region · Sales Unit · Country · Province · Domestic/Oversea · by job entry date
            </p>
          </div>

          <ReportDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={({ startDate, endDate }) => { setDateRange({ startDate, endDate }); setDetailedPage(1); }}
          />

          <Button onClick={() => setSidebarOpen(o => !o)} variant="secondary">
            <FiFilter size={14} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-primary-600 tw-text-white tw-text-xs tw-font-bold">{activeFilterCount}</span>
            )}
          </Button>

          <Button onClick={refreshAll} variant="ghost" className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-500 hover:tw-text-gray-800">
            <FiRefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {anyError && (
        <div className="tw-px-5 tw-pt-4">
          <Alert type="error" message="Some sections failed to load. Try refreshing." dismissible />
        </div>
      )}

      {/* ── Main layout: sidebar + content ── */}
      <div className="tw-flex tw-gap-5 tw-px-5 tw-py-5">
        <div className={`tw-flex-shrink-0 tw-w-64 tw-transition-all tw-duration-300 ${sidebarOpen ? 'tw-block' : 'tw-hidden md:tw-block'}`}>
          <div className="tw-sticky" style={{ top: 'calc(var(--falcon-top-nav-height, 69px) + 76px)' }}>
            <JobEntryFilterSidebar
              filterOptions={filterOptions.data}
              loading={filterOptions.loading}
              filters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>
        </div>

        <div className="tw-flex-1 tw-min-w-0 tw-space-y-5">
          {/* 1. KPI summary */}
          <JobEntrySummaryCards data={summary.data} loading={summary.loading} onDrillDown={openDrillDown} />

          {/* 2. Classification (J/P/L) + Domestic vs Oversea */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <JobEntryCategoryChart
              data={category.data}
              loading={category.loading}
              error={category.error}
              onDrillDown={openDrillDown}
            />
            <JobEntryDomesticOverseaChart
              data={domesticOversea.data}
              loading={domesticOversea.loading}
              error={domesticOversea.error}
              onDrillDown={openDrillDown}
            />
          </div>

          {/* 2b. Classification split by Domestic / Oversea */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            {(categoryByType.data || []).map((t) => (
              <JobEntryCategoryChart
                key={t.type}
                title={`Classification — ${t.type}`}
                data={t.categories}
                loading={categoryByType.loading}
                error={categoryByType.error}
                onDrillDown={openDrillDown}
                extraDrill={{ type_of_sales: t.type }}
              />
            ))}
          </div>

          {/* Classification note */}
          <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3">
            <span className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-xl tw-bg-amber-400/20 tw-text-amber-600">
              <FiAlertCircle size={20} />
            </span>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
              <span className="tw-font-bold tw-text-gray-800">Note:</span> Jobs (J) includes DP received,
              bank guarantee letter of credit, contract sign, contract with Pending or PO status.
            </p>
          </div>

          {/* 3. Trend */}
          <QuotationTrendChart
            title="Job Entry Trend"
            data={trend.data}
            loading={trend.loading}
            error={trend.error}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            onDrillDown={handleTrendDrill}
          />

          {/* 4. Region + Sales Unit */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <QuotationBreakdownChart
              title="By Sales Region" Icon={FiGlobe} iconColor="tw-text-emerald-500"
              rowLabel="Region" labelKey="region" drillKey="region" drillTitlePrefix="Region"
              data={region.data} loading={region.loading} error={region.error}
              onItemClick={openDrillDown} fullHeight
            />
            <QuotationBreakdownChart
              title="By Sales Unit" Icon={FiBriefcase} iconColor="tw-text-blue-500"
              rowLabel="Sales Unit" labelKey="unit_name" subLabelKey="region" drillKey="sales_unit_name" drillTitlePrefix="Sales Unit"
              data={salesUnit.data} loading={salesUnit.loading} error={salesUnit.error}
              onItemClick={handleUnitDrill} fullHeight
            />
          </div>

          {/* 5. Country + Province (project by country) */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <QuotationBreakdownChart
              title="By Country" Icon={FiMap} iconColor="tw-text-cyan-500"
              rowLabel="Country" labelKey="country" drillKey="country" drillTitlePrefix="Country"
              data={country.data} loading={country.loading} error={country.error}
              onItemClick={openDrillDown} fullHeight
            />
            <QuotationBreakdownChart
              title="By Province" Icon={FiMapPin} iconColor="tw-text-rose-500"
              rowLabel="Province" labelKey="province" subLabelKey="country" drillKey="province" drillTitlePrefix="Province"
              data={province.data} loading={province.loading} error={province.error}
              onItemClick={openDrillDown} fullHeight
              countryFilter={provinceCountry}
              countryOptions={(country.data || []).map(c => c.country).filter(Boolean)}
              onCountryFilterChange={setProvinceCountry}
            />
          </div>

          {/* 6. Detailed records — server-side search / sort / pagination / export */}
          <JobEntryDetailedTable
            rows={detailed.rows}
            totalRows={detailed.totalRows}
            page={detailed.page}
            perPage={detailedPer}
            loading={detailed.loading}
            error={detailed.error}
            search={tableSearch}
            onPageChange={setDetailedPage}
            onPerPageChange={(n) => { setDetailedPer(n); setDetailedPage(1); }}
            onSearchChange={(q) => { setTableSearch(q); setDetailedPage(1); }}
            onSortChange={(by, dir) => { setTableSort({ by, dir }); setDetailedPage(1); }}
            onExport={handleExportExcel}
            exportLoading={exportLoading}
          />
        </div>
      </div>

      <JobEntryDrillDownPanel
        open={drillDown.open}
        title={drillDown.title}
        baseParams={baseParams}
        extraParams={drillDown.extraParams}
        onClose={() => setDrillDown(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default JobEntryDashboard;
